import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faRocket, faChartLine, faUsers, faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PublisherSubmenu from '../../component/section/publishers/PublisherSubmenu';

function PublishersDatabasePage() {

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <>
      <Helmet prioritizeSeoTags>
        <meta charSet="utf-8" />
        <title>Search Our Influencer Database For Free | Glitch Gaming</title>
        <meta name="description" content="Search for thousands of gaming influencers for free in our easy-to-use database." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Search Our Influencer Database For Free | Glitch" />
        <meta property="og:description" content="Search for thousands of gaming influencers for free in our easy-to-use database." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/banner_1.png" />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="database-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 text-center text-lg-start">
              <h1 className="display-4 fw-bold mb-4 pt-5">
                Find Perfect Gaming Influencers <span className="text-gradient">In Minutes</span>
              </h1>
              <p className="lead mb-5">Access 25,000+ gaming creators across YouTube, Twitch, TikTok & Twitter. Filter by niche, audience size, engagement rate & more.</p>
              <div className="cta-group">
                <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3 mb-3">
                  Start Free Search Now <FontAwesomeIcon icon={faRocket} className="ms-2" />
                </Link>
                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                  Book Demo Video
                </Link>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-visual mt-5 mt-lg-0">
                <img
                  src="/assets/images/publishers/influencer_database.gif"
                  alt="Database Preview"
                  className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                />
                <div className="highlight-card youtube">
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  12,500+ YouTube Creators
                </div>
                <div className="highlight-card twitch">
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  8,200+ Twitch Streamers
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Grid */}
      <section className="value-propositions py-5 text-black">
        <div className="container">
          <div className="row g-4">
            <div className="col-md-4">
              <div className="value-card h-100">
                <div className="icon-wrapper bg-primary">
                  <FontAwesomeIcon icon={faSearch} className="fa-2x" />
                </div>
                <h3 className='text-black'>Advanced Search Filters</h3>
                <ul className="value-list">
                  <li>Platform-specific metrics</li>
                  <li>Audience demographics</li>
                  <li>Engagement analytics</li>
                  <li>Content category tags</li>
                </ul>
              </div>
            </div>
            <div className="col-md-4">
              <div className="value-card h-100">
                <div className="icon-wrapper bg-success">
                  <FontAwesomeIcon icon={faFilter} className="fa-2x" />
                </div>
                <h3 className='text-black'>Smart Matching</h3>
                <ul className="value-list">
                  <li>AI-powered recommendations</li>
                  <li>Historical performance data</li>
                  <li>Brand affinity scoring</li>
                  <li>Campaign success prediction</li>
                </ul>
              </div>
            </div>
            <div className="col-md-4">
              <div className="value-card h-100">
                <div className="icon-wrapper bg-danger">
                  <FontAwesomeIcon icon={faEnvelopeOpenText} className="fa-2x" />
                </div>
                <h3 className='text-black'>Automated Outreach</h3>
                <ul className="value-list">
                  <li>Personalized templates</li>
                  <li>Response tracking</li>
                  <li>Follow-up automation</li>
                  <li>Performance analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Showcase */}
      <section className="platform-showcase py-5 bg-dark">
        <div className="container">
          <h2 className="section-title text-center mb-5">Track All Platforms in One Place</h2>

          <div className="ratio ratio-16x9 border border-white border-2">
            <iframe
              src="https://www.youtube.com/embed/gqyMALpUcNQ"
              allowFullScreen
              title="YouTube Video"
            ></iframe>
          </div>
        </div>
      </section>

     {/* How It Works */}
<section className="workflow-steps py-5 text-black">
  <div className="container">
    <div className="text-center mb-5">
      <h2 className="display-5 fw-bold mb-3 text-black">Transform Your Marketing in 3 Simple Steps</h2>
      <p className="lead">Streamlined process designed for maximum efficiency</p>
    </div>
    
    <div className="row g-5 position-relative">
      {/* Progress Line */}
      <div className="progress-line position-absolute top-50 start-0 end-0 translate-middle-y d-none d-lg-block">
        <div className="line"></div>
      </div>

      {/* Step 1 */}
      <div className="col-lg-4">
        <div className="step-card bg-white p-4 p-lg-5 rounded-4 shadow-lg h-100">
          <div className="step-header d-flex align-items-center mb-4">
            <div className="step-number bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
              1
            </div>
            <h3 className="h4 mb-0 ms-3 text-black">Search & Filter</h3>
          </div>
          <p className="text-muted mb-4">Use our advanced filters to find perfect matches</p>
          <div className="step-visual overflow-hidden rounded-3">
            <img 
              src="/assets/images/publishers/filter_influencers.gif" 
              alt="Search Interface" 
              className="img-fluid rounded-2"
            />
          </div>
          <div className="step-features mt-4">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-sliders-h me-2 text-primary"></i>
              <span>50+ Filter Criteria</span>
            </div>
            <div className="d-flex align-items-center">
              <i className="fas fa-bolt me-2 text-primary"></i>
              <span>Instant Results</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2 */}
      <div className="col-lg-4">
        <div className="step-card bg-white p-4 p-lg-5 rounded-4 shadow-lg h-100">
          <div className="step-header d-flex align-items-center mb-4">
            <div className="step-number bg-success text-white rounded-circle d-flex align-items-center justify-content-center">
              2
            </div>
            <h3 className="h4 mb-0 ms-3 text-black">Set Your Pricing</h3>
          </div>
          <p className="text-muted mb-4">Flexible payment models that suit your needs</p>
          <div className="step-visual overflow-hidden rounded-3">
            <img 
              src="/assets/images/publishers/build_rate_card.gif" 
              alt="Analytics Dashboard" 
              className="img-fluid rounded-2"
            />
          </div>
          <div className="step-features mt-4">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-hand-holding-usd me-2 text-success"></i>
              <span>Performance-Based Options</span>
            </div>
            <div className="d-flex align-items-center">
              <i className="fas fa-chart-line me-2 text-success"></i>
              <span>ROI Predictions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3 */}
      <div className="col-lg-4">
        <div className="step-card bg-white p-4 p-lg-5 rounded-4 shadow-lg h-100">
          <div className="step-header d-flex align-items-center mb-4">
            <div className="step-number bg-danger text-white rounded-circle d-flex align-items-center justify-content-center">
              3
            </div>
            <h3 className="h4 mb-0 ms-3 text-black">Connect & Track</h3>
          </div>
          <p className="text-muted mb-4">Automated outreach with real-time tracking</p>
          <div className="step-visual overflow-hidden rounded-3">
            <img 
              src="/assets/images/publishers/performance_based.gif" 
              alt="Outreach Interface" 
              className="img-fluid rounded-2"
            />
          </div>
          <div className="step-features mt-4">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-envelope-open-text me-2 text-danger"></i>
              <span>Automated Outreach</span>
            </div>
            <div className="d-flex align-items-center">
              <i className="fas fa-chart-pie me-2 text-danger"></i>
              <span>Live Analytics</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<style jsx>{`
.workflow-steps {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%);
}

.step-number {
  width: 50px;
  height: 50px;
  font-size: 1.5rem;
  font-weight: 700;
  transition: all 0.3s ease;
}

.step-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  background: white;
  border: 1px solid rgba(0,0,0,0.05);
}

.step-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.15);
}

.progress-line {
  height: 4px;
  width: 60%;
  left: 20%;
}

.progress-line .line {
  background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
  height: 100%;
  width: 100%;
  position: relative;
}

.progress-line .line::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background: #9400ff;
  border-radius: 50%;
}

@media (max-width: 992px) {
  .progress-line {
    display: none;
  }
}

.step-visual {
  border: 1px solid rgba(0,0,0,0.1);
  background: #fff;
}

.step-features {
  border-top: 1px solid rgba(0,0,0,0.05);
  padding-top: 1rem;
}
`}</style>

      {/* CTA Section */}
      <section className="database-cta py-5">
        <div className="container text-center">
          <h2 className="mb-4">Start Finding Influencers Now</h2>
          <p className="cta-subtitle mb-5">Join 500+ game developers already boosting their marketing ROI</p>
          <div className="cta-wrapper">
            <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg px-5 py-3 me-3">
              Get Started Free
            </Link>
            <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg px-5 py-3">
              Schedule Personalized Demo
            </Link>
          </div>
          <div className="trust-badges mt-4">
            <div className="badge-item d-flex align-items-center justify-content-center gap-2">
              <i className="fas fa-shield-alt"></i>
              <span>100% GDPR Compliant</span>
            </div>
            <div className="badge-item d-flex align-items-center justify-content-center gap-2 mt-2">
              <i class="fas fa-clock"></i>
              <span>Daily Data Updates</span>
            </div>
          </div>
        </div>
      </section>

      <PublisherSubmenu />
      <Footer />

      <style jsx>{`
        .database-hero {
          padding: 100px 0;
          background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
          color: white;
          position: relative;
          overflow: hidden;
        }

        .text-gradient {
          background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero-visual {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          transform: perspective(1000px) rotateY(-5deg);
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
        }

        .highlight-card {
          position: absolute;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          backdrop-filter: blur(10px);
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          max-width: 250px;
          animation: float 6s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        .value-propositions {
          background: #f8f9ff;
          padding: 80px 0;
        }

        .value-card {
          background: white;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }

        .value-card:hover {
          transform: translateY(-10px);
        }

        .icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .platform-showcase {
          padding: 100px 0;
          background: #080e37;
        }

        .workflow-steps {
          padding: 100px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%);
        }

        .database-cta {
          padding: 100px 0;
          background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
          color: white;
        }

        @media (max-width: 768px) {
          .database-hero {
            padding: 60px 0;
          }
          
          .hero-visual {
            transform: none;
          }
        }
      `}</style>
    </>
  );
}

export default PublishersDatabasePage;